import '../styles/Calendar.css'
import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import { auth, db } from "../config/firebase";
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
    authorizeGoogleCalendar,
    isGoogleCalendarEnabled
} from '../config/googleCalendarApi';

// Import components
import VisitDialog from '../components/calendar/VisitDialog';
import EventDialog from '../components/calendar/EventDialog';
import DeleteEventDialog from '../components/calendar/DeleteEventDialog';
import DeleteSeriesDialog from '../components/calendar/DeleteSeriesDialog';
import DisconnectDialog from '../components/calendar/DisconnectDialog';
import CalendarHeader from '../components/calendar/CalendarHeader';

// Import utilities
import { generateRecurringDates, getTodoColor, isPastDate } from '../components/calendar/utils/EventUtils';
import { 
    handleEventDrop, 
    handleEventResize, 
    handleEventClick, 
    handleDateSelect,
    deleteEventFromGoogleCalendar
} from '../components/calendar/utils/EventHandlers';


const Calendar = () => {
    const [events, setEvents] = useState([]);
    const [todos, setTodos] = useState([]);
    const [eventDialog, setEventDialog] = useState(false);
    const [deleteEventDialog, setDeleteEventDialog] = useState(false);
    const [deleteSeriesDialog, setDeleteSeriesDialog] = useState(false);
    const [googleCalendarEnabled, setGoogleCalendarEnabled] = useState(false);
    const [disconnectDialog, setDisconnectDialog] = useState(false);
    const [visitDialog, setVisitDialog] = useState(false);
    const [visitData, setVisitData] = useState(null);
    const [visitViewMode, setVisitViewMode] = useState(false);
    const [userInitials, setUserInitials] = useState({});
    const currentUser = auth.currentUser;
    const repeatOptions = [
        { name: 'Daily', value: 'daily' },
        { name: 'Weekdays only', value: 'weekdays' },
        { name: 'Weekends only', value: 'weekends' },
        { name: 'Weekly', value: 'weekly' },
        { name: 'Every 2 weeks', value: 'biweekly' },
        { name: 'Monthly', value: 'monthly' }
    ];
    const [currentView, setCurrentView] = useState('dayGridMonth'); // Default view
    // Track small screens to force Day view and hide other view buttons
    const [isSmallScreen, setIsSmallScreen] = useState(
        typeof window !== 'undefined' ? window.innerWidth < 630 : false
    );

    const [event, setEvent] = useState({
        id: null,
        title: '',
        start: null,
        end: null,
        allDay: false,
        description: '',
        backgroundColor: '#1976D2',
        linkedTodoId: null,
        repeat: false,
        repeatType: 'daily',
        repeatUntil: null,
        parentEventId: null
    });
    const [selectedEvent, setSelectedEvent] = useState(null);
    const toast = useRef(null);
    const calendarRef = useRef(null);

    // Add this helper function at the beginning of your component
    const showToast = (severity, summary, detail) => {
        if (toast.current) {
            const life = severity === 'error' ? 5000 : severity === 'success' ? 3000 : 4000;
            toast.current.show({ severity, summary, detail, life });
        }
    };
    // Event colors
    const eventColors = [
        { name: 'orange', value: '#F5703D' },
        { name: 'light orange', value: '#FDD4C4' },
        { name: 'blue', value: '#C8D9E9' },
        { name: 'grey', value: '#5D5E5F' },
        { name: 'red', value: '#d32B2b' }
    ];

    useEffect(() => {
        let cleanup = null;

        const initializeListeners = async () => {
            cleanup = await setupRealTimeListeners();
            // Fetch events (including visits) on component mount
            if (currentUser) {
                fetchEvents();
            }
        };

        initializeListeners();

        // Return cleanup function
        return () => {
            if (cleanup && typeof cleanup === 'function') {
                cleanup();
            }
        };
    }, [currentUser]);

    // Listen for window resize to toggle small-screen mode
    useEffect(() => {
        const onResize = () => setIsSmallScreen(window.innerWidth < 630);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // When small-screen mode toggles, force appropriate calendar view
    useEffect(() => {
        const api = calendarRef.current?.getApi?.();
        if (!api) return;
        if (isSmallScreen) {
            if (api.view?.type !== 'timeGridDay') {
                api.changeView('timeGridDay');
            }
        } else {
            // When leaving small mode, prefer Month as default if we were stuck in Day
            if (api.view?.type === 'timeGridDay') {
                api.changeView('dayGridMonth');
            }
        }
    }, [isSmallScreen]);

    // Function to fetch user information and get initials
    const fetchUserInitials = async (userIds) => {
        try {
            const uniqueUserIds = [...new Set(userIds)];
            const initials = {};

            for (const userId of uniqueUserIds) {
                const response = await axios.get(`https://api.frazza.online/user/${userId}`);
                if (response.data.success && response.data.data) {
                    const user = response.data.data;
                    const name = user.name || user.email || '';
                    // Get first letter of name or email
                    initials[userId] = name.charAt(0).toUpperCase();
                }
            }

            setUserInitials(initials);
        } catch (error) {
            console.error("Error fetching user information:", error);
        }
    };

    // At the beginning of your Calendar component, modify the useEffect:
    useEffect(() => {
        const checkCalendarSync = async () => {
            if (currentUser) {
                try {
                    const response = await axios.post('https://api.frazza.online/calendar/check-sync', {
                        userId: currentUser.uid
                    });
                    setGoogleCalendarEnabled(response.data.enabled);
                } catch (error) {
                    console.error("Error checking calendar status:", error);
                }
            }
        };

        checkCalendarSync();
    }, [currentUser]);



    // Add a function to check if automatic sync is needed
    const checkAutoSync = async () => {
        if (!googleCalendarEnabled) return;

        try {
            const userPref = await axios.post('https://api.frazza.online/calendar/check-sync', {
                userId: currentUser.uid
            });

            if (userPref.data.enabled) {
                // Check if last sync was more than 15 minutes ago
                const lastSync = userPref.data.lastSync;
                if (!lastSync || (new Date() - new Date(lastSync)) > 15 * 60 * 1000) {
                    console.log("Auto-syncing with Google Calendar...");

                    // Perform sync in background
                    axios.post('https://api.frazza.online/calendar/sync', {
                        userId: currentUser.uid
                    }).then(response => {
                        if (response.data.success) {
                            console.log("Auto-sync completed successfully");
                            fetchEvents(); // Refresh events after sync
                        }
                    }).catch(error => {
                        console.error("Auto-sync error:", error);
                    });
                }
            }
        } catch (error) {
            console.error("Error checking for auto-sync:", error);
        }
    };

    const fetchEvents = async () => {
        try {
            const response = await axios.get('https://api.frazza.online/calendar/events', {
                params: { userId: currentUser.uid }
            });

            if (response.data.success) {
                const eventsList = response.data.events || [];
                const recurringTemplates = response.data.recurringTemplates || {};

                // Generate recurring event instances
                for (const templateId in recurringTemplates) {
                    const template = recurringTemplates[templateId];
                    if (template) {
                        const occurrences = generateRecurringDates(template);

                        occurrences.forEach((occurrence, index) => {
                            if (index > 0) {
                                occurrence.id = `generated-${template.id}-${index}`;
                            }
                            eventsList.push(occurrence);
                        });
                    }
                }

                // Update events state, preserving todos
                setEvents(prevEvents => {
                    // Keep todos from previous state
                    const todoEvents = prevEvents.filter(event => event.extendedProps?.isTodo);

                    // Filter out any duplicate events by ID
                    const eventMap = new Map();

                    // Add non-todo events from the API response
                    eventsList
                        .filter(event => !event.extendedProps?.isTodo)
                        .forEach(event => {
                            if (event && event.id) {
                                eventMap.set(event.id, event);
                            }
                        });

                    // Convert map values back to array
                    const newNonTodoEvents = Array.from(eventMap.values());

                    // Combine todos and non-todo events
                    return [...todoEvents, ...newNonTodoEvents];
                });

                // Collect user IDs from events to fetch their initials
                const userIds = eventsList
                    .filter(event => !event.extendedProps?.isTodo)
                    .map(event => event.userId)
                    .filter(userId => userId); // Filter out undefined or null values

                // Get current user's partner ID
                try {
                    const userResponse = await axios.get(`https://api.frazza.online/user/${currentUser.uid}`);
                    if (userResponse.data.success && userResponse.data.data) {
                        const userData = userResponse.data.data;
                        if (userData.inRelationWith) {
                            // Add partner ID to the list of user IDs
                            userIds.push(userData.inRelationWith);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching user information:", error);
                }

                if (userIds.length > 0) {
                    fetchUserInitials(userIds);
                }

                // Check if we need to auto-sync with Google Calendar
                checkAutoSync();
            } else {
                showToast('error', 'Error', response.data.message || 'Failed to load events');
            }
        } catch (error) {
            console.error("Error fetching events:", error);
            showToast('error', 'Error', `Failed to load events: ${error.response?.data?.error || error.message}`);
        }
    };
    // Add a function to handle saving the visit
    const saveVisit = async (visitData) => {
        // Check if the visit belongs to the current user
        if (visitData.id) {
            const visitEvent = events.find(e => e.id === visitData.id);
            if (visitEvent) {
                const visitUserId = visitEvent.extendedProps?.userId || visitEvent._def?.extendedProps?.userId;
                if (visitUserId && visitUserId !== currentUser.uid) {
                    showToast('warn', 'Not Allowed', 'You cannot edit or delete your partner\'s events');
                    return;
                }
            }
        }

        try {
            const response = await axios.post('https://api.frazza.online/calendar/save-visit', {
                userId: currentUser.uid,
                visitData: {
                    title: visitData.title,
                    arrival: visitData.arrival,
                    departure: visitData.departure,
                    allDay: visitData.allDay,
                    description: visitData.description,
                    backgroundColor: visitData.backgroundColor,
                    borderColor: visitData.borderColor,      // <-- add this
                    textColor: visitData.textColor,          // <-- add this
                    linkedTodoId: visitData.linkedTodoId,
                    repeat: visitData.repeat,
                    repeatType: visitData.repeat ? visitData.repeatType : null,
                    repeatUntil: visitData.repeat ? visitData.repeatUntil : null,
                    parentEventId: visitData.parentEventId,
                    reminders: visitData.reminders,
                    dailyPlans: visitData.dailyPlans,
                    tickets: visitData.tickets
                }
            });

            if (response.data.success) {
                showToast('success', 'Success', 'Visit planned successfully');

                // If Google Calendar is enabled, trigger a sync
                if (googleCalendarEnabled) {
                    try {
                        axios.post('https://api.frazza.online/calendar/sync', {
                            userId: currentUser.uid
                        }).then(() => {
                            console.log("Visit synced with Google Calendar");
                            fetchEvents(); // Refresh events after sync
                        }).catch(error => {
                            console.error("Error syncing visit with Google Calendar:", error);
                            fetchEvents(); // Still refresh events even if sync fails
                        });
                    } catch (syncError) {
                        console.error("Error initiating visit sync:", syncError);
                        fetchEvents(); // Still refresh events even if sync fails
                    }
                } else {
                    fetchEvents();
                }
            } else {
                showToast('error', 'Error', response.data.message || 'Failed to save visit');
            }
        } catch (error) {
            console.error("Error saving visit:", error);
            showToast('error', 'Error', 'Failed to save visit');
        }
    };
    // Fetch todos from the server
    const fetchTodos = async () => {
        try {
            const response = await axios.get('https://api.frazza.online/todos', {
                params: { userId: currentUser.uid }
            });

            const userTodos = response.data.userTodos || [];
            const partnerTodos = response.data.partnerTodos || [];
            const allTodos = [...userTodos, ...partnerTodos];

            setTodos(allTodos);
            updateCalendarEvents(allTodos);
        } catch (error) {
            console.error("Error fetching todos:", error);
            showToast('error', 'Error', `Failed to load todos: ${error.response?.data?.error || error.message}`);
        }
    };

    // Set up real-time Firestore listeners for events, visits, and todos
    const setupRealTimeListeners = async () => {
        if (!currentUser) return;

        const unsubscribers = [];

        try {
            // Get user info to find partner
            const userInfoDoc = await getDoc(doc(db, "UserInformation", currentUser.uid));
            let partnerId = null;

            if (userInfoDoc.exists()) {
                const userData = userInfoDoc.data();
                partnerId = userData.inRelationWith;
            }

            // Set up listener for current user's events
            const userEventsRef = collection(db, "events", currentUser.uid, "userEvents");
            const userEventsListener = onSnapshot(userEventsRef, (snapshot) => {
                const userEvents = [];
                snapshot.forEach(doc => {
                    const data = doc.data();
                    userEvents.push({
                        id: doc.id,
                        ...data,
                        start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                        end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                        repeatUntil: data.repeatUntil?.toDate ? data.repeatUntil.toDate() : data.repeatUntil,
                        userId: currentUser.uid,
                        extendedProps: {
                            ...data.extendedProps,
                            isTodo: false
                        }
                    });
                });

                // Process recurring events
                const processedEvents = [];
                const recurringTemplates = {};

                userEvents.forEach(event => {
                    if (!event.repeat) {
                        processedEvents.push(event);
                    } else if (!event.parentEventId) {
                        recurringTemplates[event.id] = event;
                    } else {
                        processedEvents.push(event);
                    }
                });

                // Generate recurring event instances
                for (const templateId in recurringTemplates) {
                    const template = recurringTemplates[templateId];
                    const occurrences = generateRecurringDates(template);

                    occurrences.forEach((occurrence, index) => {
                        if (index > 0) {
                            occurrence.id = `generated-${template.id}-${index}`;
                        }
                        processedEvents.push(occurrence);
                    });
                }

                // Update events state
                setEvents(prevEvents => {
                    const todoEvents = prevEvents.filter(event => event.extendedProps?.isTodo);
                    const partnerEvents = prevEvents.filter(event => event.userId !== currentUser.uid && !event.extendedProps?.isTodo);
                    const visitEvents = prevEvents.filter(event => event.extendedProps?.visitDetails);
                    return [...todoEvents, ...partnerEvents, ...visitEvents, ...processedEvents];
                });
            }, (error) => {
                console.error("Error listening to user events:", error);
                showToast('error', 'Error', 'Failed to load calendar events');
            });
            unsubscribers.push(userEventsListener);

            // Set up listener for partner's events if partner exists
            if (partnerId) {
                const partnerEventsRef = collection(db, "events", partnerId, "userEvents");
                const partnerEventsListener = onSnapshot(partnerEventsRef, (snapshot) => {
                    const partnerEvents = [];
                    snapshot.forEach(doc => {
                        const data = doc.data();
                        partnerEvents.push({
                            id: doc.id,
                            ...data,
                            start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                            end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                            repeatUntil: data.repeatUntil?.toDate ? data.repeatUntil.toDate() : data.repeatUntil,
                            userId: partnerId,
                            extendedProps: {
                                ...data.extendedProps,
                                isTodo: false
                            }
                        });
                    });

                    // Process recurring events for partner
                    const processedPartnerEvents = [];
                    const partnerRecurringTemplates = {};

                    partnerEvents.forEach(event => {
                        if (!event.repeat) {
                            processedPartnerEvents.push(event);
                        } else if (!event.parentEventId) {
                            partnerRecurringTemplates[event.id] = event;
                        } else {
                            processedPartnerEvents.push(event);
                        }
                    });

                    // Generate recurring event instances for partner
                    for (const templateId in partnerRecurringTemplates) {
                        const template = partnerRecurringTemplates[templateId];
                        const occurrences = generateRecurringDates(template);

                        occurrences.forEach((occurrence, index) => {
                            if (index > 0) {
                                occurrence.id = `generated-partner-${template.id}-${index}`;
                            }
                            processedPartnerEvents.push(occurrence);
                        });
                    }

                    // Update events state with partner events
                    setEvents(prevEvents => {
                        const todoEvents = prevEvents.filter(event => event.extendedProps?.isTodo);
                        const userEvents = prevEvents.filter(event => event.userId === currentUser.uid && !event.extendedProps?.isTodo);
                        const visitEvents = prevEvents.filter(event => event.extendedProps?.visitDetails);
                        return [...todoEvents, ...userEvents, ...visitEvents, ...processedPartnerEvents];
                    });
                }, (error) => {
                    console.error("Error listening to partner events:", error);
                });
                unsubscribers.push(partnerEventsListener);
            }

            // Set up listener for current user's visits
            const userVisitsRef = collection(db, "events", currentUser.uid, "userVisits");
            const userVisitsListener = onSnapshot(userVisitsRef, (snapshot) => {
                const userVisits = [];
                snapshot.forEach(doc => {
                    const data = doc.data();
                    userVisits.push({
                        id: doc.id,
                        ...data,
                        start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                        end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                        repeatUntil: data.repeatUntil?.toDate ? data.repeatUntil.toDate() : data.repeatUntil,
                        userId: currentUser.uid,
                        extendedProps: {
                            ...data.extendedProps,
                            isTodo: false,
                            visitDetails: data.visitDetails || {
                                reminders: data.reminders,
                                dailyPlans: data.dailyPlans,
                                tickets: data.tickets
                            }
                        }
                    });
                });

                // Process recurring visits
                const processedVisits = [];
                const recurringVisitTemplates = {};

                userVisits.forEach(visit => {
                    if (!visit.repeat) {
                        processedVisits.push(visit);
                    } else if (!visit.parentEventId) {
                        recurringVisitTemplates[visit.id] = visit;
                    } else {
                        processedVisits.push(visit);
                    }
                });

                // Generate recurring visit instances
                for (const templateId in recurringVisitTemplates) {
                    const template = recurringVisitTemplates[templateId];
                    const occurrences = generateRecurringDates(template);

                    occurrences.forEach((occurrence, index) => {
                        if (index > 0) {
                            occurrence.id = `generated-visit-${template.id}-${index}`;
                        }
                        processedVisits.push(occurrence);
                    });
                }

                // Update events state with user visits
                setEvents(prevEvents => {
                    const todoEvents = prevEvents.filter(event => event.extendedProps?.isTodo);
                    const regularEvents = prevEvents.filter(event => !event.extendedProps?.isTodo && !event.extendedProps?.visitDetails);
                    const partnerVisits = prevEvents.filter(event => 
                        event.extendedProps?.visitDetails && event.userId !== currentUser.uid);

                    return [...todoEvents, ...regularEvents, ...partnerVisits, ...processedVisits];
                });
            }, (error) => {
                console.error("Error listening to user visits:", error);
                showToast('error', 'Error', 'Failed to load visits');
            });
            unsubscribers.push(userVisitsListener);

            // Set up listener for partner's visits if partner exists
            if (partnerId) {
                const partnerVisitsRef = collection(db, "events", partnerId, "userVisits");
                const partnerVisitsListener = onSnapshot(partnerVisitsRef, (snapshot) => {
                    const partnerVisits = [];
                    snapshot.forEach(doc => {
                        const data = doc.data();
                        partnerVisits.push({
                            id: doc.id,
                            ...data,
                            start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                            end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                            repeatUntil: data.repeatUntil?.toDate ? data.repeatUntil.toDate() : data.repeatUntil,
                            userId: partnerId,
                            extendedProps: {
                                ...data.extendedProps,
                                isTodo: false,
                                visitDetails: data.visitDetails || {
                                    reminders: data.reminders,
                                    dailyPlans: data.dailyPlans,
                                    tickets: data.tickets
                                }
                            }
                        });
                    });

                    // Process recurring visits for partner
                    const processedPartnerVisits = [];
                    const partnerRecurringVisitTemplates = {};

                    partnerVisits.forEach(visit => {
                        if (!visit.repeat) {
                            processedPartnerVisits.push(visit);
                        } else if (!visit.parentEventId) {
                            partnerRecurringVisitTemplates[visit.id] = visit;
                        } else {
                            processedPartnerVisits.push(visit);
                        }
                    });

                    // Generate recurring visit instances for partner
                    for (const templateId in partnerRecurringVisitTemplates) {
                        const template = partnerRecurringVisitTemplates[templateId];
                        const occurrences = generateRecurringDates(template);

                        occurrences.forEach((occurrence, index) => {
                            if (index > 0) {
                                occurrence.id = `generated-partner-visit-${template.id}-${index}`;
                            }
                            processedPartnerVisits.push(occurrence);
                        });
                    }

                    // Update events state with partner visits
                    setEvents(prevEvents => {
                        const todoEvents = prevEvents.filter(event => event.extendedProps?.isTodo);
                        const regularEvents = prevEvents.filter(event => !event.extendedProps?.isTodo && !event.extendedProps?.visitDetails);
                        const userVisits = prevEvents.filter(event => 
                            event.extendedProps?.visitDetails && event.userId === currentUser.uid);

                        return [...todoEvents, ...regularEvents, ...userVisits, ...processedPartnerVisits];
                    });
                }, (error) => {
                    console.error("Error listening to partner visits:", error);
                });
                unsubscribers.push(partnerVisitsListener);
            }

            // Set up listener for current user's todos
            const userTodosRef = collection(db, "events", currentUser.uid, "userTodos");
            const userTodosListener = onSnapshot(userTodosRef, (snapshot) => {
                const userTodos = [];
                snapshot.forEach(doc => {
                    const data = doc.data();
                    const dueDate = data.date ? new Date(data.date) : null;
                    userTodos.push({
                        ...data,
                        id: doc.id,
                        dueDate: dueDate,
                        isCurrentUser: true
                    });
                });

                // Update todos state and calendar events
                setTodos(prevTodos => {
                    const partnerTodos = prevTodos.filter(todo => !todo.isCurrentUser);
                    const allTodos = [...userTodos, ...partnerTodos];
                    updateCalendarEvents(allTodos);
                    return allTodos;
                });
            }, (error) => {
                console.error("Error listening to user todos:", error);
            });
            unsubscribers.push(userTodosListener);

            // Set up listener for partner's todos if partner exists
            if (partnerId) {
                const partnerTodosRef = collection(db, "events", partnerId, "userTodos");
                const partnerTodosListener = onSnapshot(partnerTodosRef, (snapshot) => {
                    const partnerTodos = [];
                    snapshot.forEach(doc => {
                        const data = doc.data();
                        const dueDate = data.date ? new Date(data.date) : null;
                        partnerTodos.push({
                            ...data,
                            id: doc.id,
                            dueDate: dueDate,
                            isCurrentUser: false
                        });
                    });

                    // Update todos state and calendar events
                    setTodos(prevTodos => {
                        const userTodos = prevTodos.filter(todo => todo.isCurrentUser);
                        const allTodos = [...userTodos, ...partnerTodos];
                        updateCalendarEvents(allTodos);
                        return allTodos;
                    });
                }, (error) => {
                    console.error("Error listening to partner todos:", error);
                });
                unsubscribers.push(partnerTodosListener);
            }

            // Get user initials for display
            const userIds = [currentUser.uid];
            if (partnerId) userIds.push(partnerId);
            await fetchUserInitials(userIds);

        } catch (error) {
            console.error("Error setting up real-time listeners:", error);
            showToast('error', 'Error', 'Failed to set up real-time updates');
        }

        // Return cleanup function
        return () => {
            unsubscribers.forEach(unsubscribe => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            });
        };
    };


    // Use the imported handleEventDrop function
    const onEventDrop = (dropInfo) => {
        handleEventDrop(dropInfo, currentUser, googleCalendarEnabled, showToast, fetchEvents);
    };


    // Use the imported handleEventResize function
    const onEventResize = (resizeInfo) => {
        handleEventResize(resizeInfo, currentUser, googleCalendarEnabled, showToast, fetchEvents);
    };

    // Use the imported handleEventClick function
    const onEventClick = (info) => {
        handleEventClick(info, currentUser, setSelectedEvent, showToast, setVisitData, setVisitViewMode, setVisitDialog, setEvent, setEventDialog);
    };

    // Use the imported handleDateSelect function
    const onDateSelect = (selectInfo) => {
        handleDateSelect(selectInfo, setEvent, setEventDialog);
    };

    useEffect(() => {
        const checkCalendarSync = async () => {
            if (currentUser) {
                const enabled = await isGoogleCalendarEnabled(currentUser.uid);
                setGoogleCalendarEnabled(enabled);
            }
        };
        checkCalendarSync();
    }, [currentUser]);

    // Save event
    const saveEvent = async () => {
        if (!event.title.trim()) {
            showToast('error', 'Error', 'Event title is required');
            return;
        }

        // Check if the event belongs to the current user
        const selectedEventUserId = selectedEvent?.extendedProps?.userId || selectedEvent?._def?.extendedProps?.userId;
        if (selectedEventUserId && selectedEventUserId !== currentUser.uid) {
            showToast('warn', 'Not Allowed', 'You cannot edit or delete your partner\'s events');
            setEventDialog(false);
            return;
        }

        try {
            const response = await axios.post('https://api.frazza.online/calendar/save-event', {
                userId: currentUser.uid,
                eventData: {
                    id: event.id,
                    title: event.title,
                    start: event.start,
                    end: event.end,
                    allDay: event.allDay,
                    description: event.description,
                    backgroundColor: event.backgroundColor,
                    linkedTodoId: event.linkedTodoId,
                    repeat: event.repeat,
                    repeatType: event.repeat ? event.repeatType : null,
                    repeatUntil: event.repeat ? event.repeatUntil : null,
                    parentEventId: event.parentEventId,
                    googleEventId: event.googleEventId
                }
            });

            if (response.data.success) {
                showToast('success', 'Success', response.data.message);
                setEventDialog(false);

                // If Google Calendar is enabled, trigger a sync
                if (googleCalendarEnabled) {
                    try {
                        // Sync in background without showing toast
                        axios.post('https://api.frazza.online/calendar/sync', {
                            userId: currentUser.uid
                        }).then(() => {
                            console.log("Event synced with Google Calendar");
                            fetchEvents(); // Refresh events after sync
                        }).catch(error => {
                            console.error("Error syncing with Google Calendar:", error);
                            fetchEvents(); // Still refresh events even if sync fails
                        });
                    } catch (syncError) {
                        console.error("Error initiating sync:", syncError);
                        fetchEvents(); // Still refresh events even if sync fails
                    }
                } else {
                    fetchEvents();
                }
            } else {
                showToast('error', 'Error', response.data.message || 'Failed to save event');
            }
        } catch (error) {
            console.error("Error saving event:", error);
            showToast('error', 'Error', `Failed to save event: ${error.response?.data?.error || error.message}`);
        }
    };

    const disconnectGoogleCalendar = async () => {
        try {
            showToast('info', 'Processing', 'Disconnecting from Google Calendar...');

            const response = await axios.post('https://api.frazza.online/calendar/disconnect-google', {
                userId: currentUser.uid
            });

            if (response.data.success) {
                setGoogleCalendarEnabled(false);
                showToast('success', 'Success', 'Disconnected from Google Calendar');
            } else {
                showToast('error', 'Error', response.data.message || 'Failed to disconnect from Google Calendar');
            }
        } catch (error) {
            console.error("Failed to disconnect from Google Calendar:", error);
            showToast('error', 'Error', `Disconnection failed: ${error.response?.data?.error || error.message}`);
        }
    };



    const deleteVisit = async (visitId) => {
        if (!visitId || !currentUser?.uid) return;

        // Find the visit (FullCalendar event object variants)
        const visitEvent = events.find(e => e.id === visitId);
        const visitUserId =
            visitEvent?.extendedProps?.userId ||
            visitEvent?._def?.extendedProps?.userId;

        // Block deleting partner's visit
        if (visitUserId && visitUserId !== currentUser.uid) {
            showToast('warn', 'Not Allowed', 'You cannot edit or delete your partner\'s events');
            return;
        }

        try {
            showToast('info', 'Deleting', 'Removing visit...');

            // OPTIONAL: if you still want to try deleting directly from Google first
            if (googleCalendarEnabled) {
                const googleEventId =
                    visitEvent?.extendedProps?.googleEventId ||
                    visitEvent?._def?.extendedProps?.googleEventId;
                if (googleEventId) {
                    try {
                        await deleteEventFromGoogleCalendar(googleEventId, currentUser);
                    } catch (err) {
                        console.warn('Google delete failed (continuing):', err);
                    }
                }
            }

            // Delete from backend (correct endpoint)
            const resp = await axios.delete(
                `https://api.frazza.online/calendar/event/${visitId}`,
                { params: { userId: currentUser.uid } }
            );

            if (!resp.data?.success) {
                showToast('error', 'Error', resp.data?.error || 'Failed to delete visit');
                await fetchEvents();
                return;
            }

            showToast('success', 'Success', 'Visit deleted');

            // Optionally optimize: remove locally instead of full refetch
            // setEvents(prev => prev.filter(e => e.id !== visitId));

            if (googleCalendarEnabled) {
                // Fire and forget sync; always refresh afterwards
                axios.post('https://api.frazza.online/calendar/sync', { userId: currentUser.uid })
                    .catch(e => console.warn('Sync after delete failed:', e))
                    .finally(() => fetchEvents());
            } else {
                fetchEvents();
            }
        } catch (error) {
            console.error('Delete visit failed:', error);
            showToast('error', 'Error', error.response?.data?.error || error.message);
            fetchEvents(); // Refresh anyway
        }
    };

    const deleteEvent = async (deleteSeries = false) => {
        // Check if the event belongs to the current user
        const selectedEventUserId = selectedEvent?.extendedProps?.userId || selectedEvent?._def?.extendedProps?.userId;
        if (selectedEventUserId && selectedEventUserId !== currentUser.uid) {
            showToast('warn', 'Not Allowed', 'You cannot edit or delete your partner\'s events');
            setDeleteEventDialog(false);
            setDeleteSeriesDialog(false);
            return;
        }

        try {
            // First, check if the event has a Google Calendar event ID
            let googleEventId = null;
            if (selectedEvent && (selectedEvent.extendedProps?.googleEventId || selectedEvent._def?.extendedProps?.googleEventId)) {
                googleEventId = selectedEvent.extendedProps?.googleEventId || selectedEvent._def?.extendedProps?.googleEventId;
            }

            // If Google Calendar is enabled and we have a Google Event ID, delete from Google Calendar first
            if (googleCalendarEnabled && googleEventId) {
                try {
                    await deleteEventFromGoogleCalendar(googleEventId, currentUser);
                    console.log("Deleted from Google Calendar:", googleEventId);
                } catch (googleError) {
                    console.error("Error deleting from Google Calendar:", googleError);
                    // Continue with deletion even if Google Calendar deletion fails
                }
            }

            // Now delete from our database
            const response = await axios.delete(`https://api.frazza.online/calendar/event/${event.id}`, {
                params: { 
                    userId: currentUser.uid,
                    deleteSeries: deleteSeries
                }
            });

            if (response.data.success) {
                showToast('success', 'Success', response.data.message);
                setDeleteEventDialog(false);
                setDeleteSeriesDialog(false);

                // If Google Calendar is enabled, trigger a sync to ensure deletion is reflected
                if (googleCalendarEnabled) {
                    try {
                        // Sync in background without showing toast
                        axios.post('https://api.frazza.online/calendar/sync', {
                            userId: currentUser.uid
                        }).then(() => {
                            console.log("Calendar synced after event deletion");
                            fetchEvents(); // Refresh events after sync
                        }).catch(error => {
                            console.error("Error syncing after deletion:", error);
                            fetchEvents(); // Still refresh events even if sync fails
                        });
                    } catch (syncError) {
                        console.error("Error initiating sync after deletion:", syncError);
                        fetchEvents(); // Still refresh events even if sync fails
                    }
                } else {
                    fetchEvents();
                }
            } else {
                showToast('error', 'Error', response.data.message || 'Failed to delete event');
            }
        } catch (error) {
            console.error("Error deleting event:", error);
            showToast('error', 'Error', `Failed to delete event: ${error.response?.data?.error || error.message}`);

            // Even if there's an error, try to refresh the events list
            // This can help if the event was actually deleted but there was an error in the response
            fetchEvents();
            setDeleteEventDialog(false);
            setDeleteSeriesDialog(false);
        }
    };


    const updateCalendarEvents = (todos) => {
        setEvents(prevEvents => {
            const regularEvents = prevEvents.filter(event => !event.extendedProps?.isTodo);

            // Convert todos to calendar events
            const todoEvents = todos
                .filter(todo => todo.date)
                .map(todo => {
                    // Check if todo is overdue
                    const todoDate = new Date(todo.date);
                    const now = new Date();
                    now.setHours(0, 0, 0, 0); // Reset to start of day for proper comparison
                    const isOverdue = todoDate < now;

                    // Update active status if needed
                    if (isOverdue && todo.active !== false) {
                        todo.active = false;
                    }

                    return {
                        id: `todo-${todo.id}`,
                        title: todo.todo || "Untitled Task",
                        start: new Date(todo.date),
                        end: new Date(todo.date),
                        allDay: true,
                        backgroundColor: getTodoColor(todo.priority),
                        borderColor: '#000000',
                        textColor: isOverdue ? '#999999' : null,
                        className: `${todo.status ? 'completed-todo' : ''} ${isOverdue ? 'overdue-todo' : ''}`,
                        extendedProps: {
                            isTodo: true,
                            todoId: todo.id,
                            completed: todo.status || false,
                            category: todo.category || '',
                            overdue: isOverdue
                        }
                    };
                });

            return [...regularEvents, ...todoEvents];
        });
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" />

            <div className="content-section">
                 <h1 className="page-title">Calendar</h1>
                 <CalendarHeader
                     onNewEvent={() => {
                         const now = new Date();
                         const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
                         setEvent({
                             id: null,
                             title: '',
                             start: now,
                             end: oneHourLater,
                             allDay: false,
                             description: '',
                             backgroundColor: '#1976D2',
                             linkedTodoId: null,
                             repeat: false,
                             repeatType: 'daily',
                             repeatUntil: null,
                             parentEventId: null
                         });
                         setEventDialog(true);
                     }}
                     onNewVisit={() => setVisitDialog(true)}
                     googleCalendarEnabled={googleCalendarEnabled}
                     setGoogleCalendarEnabled={setGoogleCalendarEnabled}
                     currentUser={currentUser}
                     showToast={showToast}
                     fetchEvents={fetchEvents}
                     disconnectDialog={disconnectDialog}
                     setDisconnectDialog={setDisconnectDialog}
                 />

                <div className="content-card">
                    <div className="sync-status">
                        <i className={`pi ${googleCalendarEnabled ? 'pi-check-circle text-success' : 'pi-times-circle text-danger'}`}></i>
                        <span className="sync-text">{googleCalendarEnabled ? 'Connected to Google Calendar' : 'Google Calendar disconnected'}</span>
                    </div>

                    <div className="calendar-wrapper">
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            initialView={isSmallScreen ? 'timeGridDay' : 'dayGridMonth'}
                            titleFormat={isSmallScreen ? { year: 'numeric', month: 'short', day: 'numeric' } : { year: 'numeric', month: 'long', day: 'numeric' }}
                            headerToolbar={{
                                left: 'prev,next today',
                                center: 'title',
                                right: isSmallScreen
                                    ? 'timeGridDay'
                                    : 'dayGridMonth,timeGridWeek,timeGridDay'
                            }}
                            editable={true}
                            displayEventTime={currentView !== 'dayGridMonth'} // Only show time when NOT in month view
                            selectable={true}
                            selectMirror={true}
                            dayMaxEvents={true}
                            events={events}
                            eventClick={onEventClick}
                            select={onDateSelect}
                            eventDrop={onEventDrop}
                            eventResize={onEventResize}
                            height="auto"
                            viewDidMount={(viewInfo) => setCurrentView(viewInfo.view.type)} // Update when view mounts
                            datesSet={(info) => setCurrentView(info.view.type)} // Update on any view change
                            eventContent={(eventInfo) => {
                                const isVisitEvent = eventInfo.event.extendedProps.visitDetails;
                                const isTodoEvent = eventInfo.event.extendedProps.isTodo;
                                const isNormalEvent = !isVisitEvent && !isTodoEvent;

                                // Get userId from either extendedProps or directly from the event
                                const userId = eventInfo.event.extendedProps.userId || eventInfo.event._def.extendedProps.userId;
                                const initial = userId ? (userInitials[userId] || '') : '';

                                // Normal events: title + initial
                                if (isNormalEvent && userId) {
                                    return (
                                        <div className="fc-event-main-wrapper normal-event">
                                            <div className="fc-event-title-container">
                                                {eventInfo.event.title}
                                                {initial && <span className="event-creator-initial">{initial}</span>}
                                            </div>
                                            {eventInfo.timeText && (
                                                <div className="fc-event-time">{eventInfo.timeText}</div>
                                            )}
                                        </div>
                                    );
                                }

                                // Visit events: user initial + title + black border
                                if (isVisitEvent) {
                                    // Get both user initials for visit events
                                    const creatorInitial = initial;

                                    // Get partner initial
                                    let partnerInitial = '';
                                    if (userId) {
                                        if (userId === currentUser.uid) {
                                            const partnerIds = Object.keys(userInitials).filter(id => id !== currentUser.uid);
                                            if (partnerIds.length > 0) {
                                                partnerInitial = userInitials[partnerIds[0]] || '';
                                            }
                                        } else {
                                            partnerInitial = userInitials[currentUser.uid] || '';
                                        }
                                    }

                                    return (
                                        <div
                                            className="fc-event-main-wrapper visit-event"
                                            style={{
                                                border: `2px solid ${eventInfo.event.backgroundColor || '#000'}`,
                                                color: eventInfo.event.textColor || 'inherit'
                                            }}
                                        >
                                            <div className="fc-event-title-container">
                                                {eventInfo.event.title}
                                                <div className="event-creator-initial">
                                                    {creatorInitial}
                                                </div>
                                            </div>
                                            {eventInfo.timeText && (
                                                <div className="fc-event-time">{eventInfo.timeText}</div>
                                            )}
                                        </div>
                                    );
                                }

                                else if (isTodoEvent) {
                                    return (
                                        <div className="todo-event">
                                            <p className="todo-dot"></p>
                                            <p>to-do</p>
                                        </div>
                                    );
                                }

                                // Fallback to default rendering
                                return null;
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Event Dialog */}
            <EventDialog
                visible={eventDialog}
                event={event}
                setEvent={setEvent}
                onHide={() => setEventDialog(false)}
                onSave={saveEvent}
                onDelete={() => {
                    setEventDialog(false);
                    if (event.repeat || event.parentEventId) {
                        setDeleteSeriesDialog(true);
                    } else {
                        setDeleteEventDialog(true);
                    }
                }}
                eventColors={eventColors}
                todos={todos}
                repeatOptions={repeatOptions}
            />

            {/* Delete Event Dialog */}
            <DeleteEventDialog
                visible={deleteEventDialog}
                onHide={() => setDeleteEventDialog(false)}
                onDelete={() => deleteEvent(false)}
            />

            {/* Delete Series Dialog */}
            <DeleteSeriesDialog
                visible={deleteSeriesDialog}
                onHide={() => setDeleteSeriesDialog(false)}
                onDeleteSingle={() => deleteEvent(false)}
                onDeleteSeries={() => deleteEvent(true)}
            />

            {/* Disconnect Dialog */}
            <DisconnectDialog
                visible={disconnectDialog}
                onHide={() => setDisconnectDialog(false)}
                onDisconnect={disconnectGoogleCalendar}
            />
            <VisitDialog
                visible={visitDialog}
                onHide={() => {
                    setVisitDialog(false);
                    setVisitViewMode(false);
                }}
                onSaveVisit={saveVisit}
                onDeleteVisit={deleteVisit}
                eventColors={eventColors}
                initialData={visitData}
                viewMode={visitViewMode}
            />
            <ConfirmDialog />
        </div>
    );
};

export default Calendar;
